import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Button, DatePicker, Row } from "antd";

import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";

import { range } from "lodash";

import { Euer } from "../../../types";
import api from "../../../api";
import {
  AccountWrapper,
  ButtonsWrapper,
  BWAHeaderContainer,
  DateFiltersBWAView,
} from "./styledComponents";

import { useLazyRequest } from "../../hooks/useRequest.hook";

import PreviewModal from "./PreviewModal";

import { downloadCSV } from "./csvHandling";
import FilterBoxCard from "../../common/Card/FilterBoxCard";
import { DateInputType } from "../../common/TransactionFilters/DateInput";
import { getBerlinMomentTimezone } from "../../common/helpers";
import { downloadPDF } from "./helpers";
import {
  buildExportFileName,
  getFileNameFromContentDisposition,
  periodFromIsoDates,
  showErrorNotification,
} from "../../../utils";
import { useKontaxUserContext } from "../../contexts/KontaxUserContext";
import { SAVE_DATE_FORMAT } from "@/v1/constants";
import CustomerSearchInput from "@/lib/customerSearch/CustomerSearchInput";

const isNotEmpty = (value: string) => !!value;
const dateFormat = "MM-YYYY";
const maxWidth = 200; // maxWidth of filers group is 200px
const maxDate = getBerlinMomentTimezone().format();
const { RangePicker } = DatePicker;

const BWAHeader = ({
  email,
  changeHandler,
}: {
  email?: string;
  changeHandler: Function;
}) => {
  const [startDate, setStartDate] = useState<string | null>(
    moment().startOf("year").format(SAVE_DATE_FORMAT)
  );
  const [endDate, setEndDate] = useState<string | null>(
    moment().format(SAVE_DATE_FORMAT)
  );
  const [accountId, setAccountId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selection, setSelection] = useState<string>("");
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const { user } = useKontaxUserContext();
  // The context keeps the previous customer while it refetches after a switch.
  const customer = user && user.email === email ? user : { email };

  const buildBwaFileName = (extension: string) =>
    buildExportFileName({
      user: customer,
      exportType: "BWA",
      period:
        startDate && endDate
          ? periodFromIsoDates(startDate, endDate)
          : undefined,
      extension,
    });

  const getCurrentYear = () => moment().get(DateInputType.Year).toString();

  const [getEuerElster, getEuerElsterResponse] = useLazyRequest<Euer>(
    (accountId: string, startDate: string, endDate: string) =>
      api.kontax.getEuerElster(accountId, startDate, endDate)
  );

  const canExportBwaPDF = email && startDate && endDate;

  const getAccountId = useCallback(async (email: string) => {
    try {
      if (isNotEmpty(email)) {
        const accountId = await api.kontax.getAccountIdByEmail(email);
        setAccountId(accountId);
      } else {
        setAccountId("");
      }
    } catch (e) {
      setAccountId("");
    }
  }, []);

  useEffect(() => {
    if (email) {
      getAccountId(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleYearSelect = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    yearSelected: string
  ): void => {
    setSelection(yearSelected);
    const isCurrentYear = yearSelected.includes(moment().year().toString());
    const dateFrom = `${yearSelected}-01-01`;
    const dateTo = isCurrentYear
      ? moment().format(SAVE_DATE_FORMAT)
      : `${yearSelected}-12-31`;
    changeHandler({ dateFrom, dateTo });
    setStartDate(moment(yearSelected).startOf("year").format(SAVE_DATE_FORMAT));
    setEndDate(
      isCurrentYear
        ? moment().format(SAVE_DATE_FORMAT)
        : moment(yearSelected).endOf("year").format(SAVE_DATE_FORMAT)
    );
  };

  const handlePreviewRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    await getEuerElster(accountId, startDate, endDate);
    setShowModal(true);
  };

  const handleDownloadCSVRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    const data = await getEuerElster(accountId, startDate, endDate);
    downloadCSV(buildBwaFileName("csv"), data.elsterFields);
  };

  const handleDownloadPDFRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canExportBwaPDF || !email) {
      return;
    }

    setIsExportingPdf(true);

    try {
      const response = await api.kontax.getBWAPdf(email, startDate, endDate);
      const filename =
        getFileNameFromContentDisposition(
          response.headers["content-disposition"]
        ) ?? buildBwaFileName("pdf");
      await downloadPDF(response.data, filename);
    } catch (err) {
      showErrorNotification({
        message: `An error occurred while generating BWA PDF: ${
          (err as Error).message
        }`,
      });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  return (
    <>
      <Row>
        <BWAHeaderContainer>
          <FilterBoxCard title="Email">
            <AccountWrapper>
              {/* Hands over the resolved customer's email, so the PDF and
                  the stats are requested for a real email (SMT-1396). */}
              <CustomerSearchInput
                id="email"
                value={email}
                onSelect={(selected) => {
                  changeHandler({ email: selected || "" });
                  getAccountId(selected || "");
                }}
              />
            </AccountWrapper>
          </FilterBoxCard>
          <FilterBoxCard>
            <DateFiltersBWAView>
              {range(+getCurrentYear(), 2019).map((yearIdx, _, arr) => {
                const yearString = "" + yearIdx;
                return (
                  <Button
                    style={{ width: `${maxWidth / arr.length}px` }}
                    key={yearString}
                    type={yearString === selection ? "primary" : "default"}
                    onClick={(e) => handleYearSelect(e, yearString)}
                  >
                    {yearString}
                  </Button>
                );
              })}
            </DateFiltersBWAView>
          </FilterBoxCard>

          <FilterBoxCard>
            <RangePicker
              style={{ width: maxWidth }}
              disabledDate={
                maxDate
                  ? (date) => !date || date.isAfter(maxDate, "day")
                  : undefined
              }
              format={dateFormat}
              value={
                startDate && endDate
                  ? [moment(startDate), moment(endDate)]
                  : undefined
              }
              onChange={(dates) => {
                setSelection("");
                if (!dates) {
                  // happens if user clears the dateRangePicker
                  setStartDate("");
                  setEndDate("");
                } else {
                  const momentStart = moment(dates[0]).startOf("month");
                  const momentEnd = moment(dates[1]).endOf("month");
                  const momentLatest = moment();
                  const effectiveEnd = momentEnd.isAfter(momentLatest)
                    ? momentLatest
                    : momentEnd;

                  changeHandler({
                    dateFrom: momentStart.format(SAVE_DATE_FORMAT),
                    dateTo: effectiveEnd.format(SAVE_DATE_FORMAT),
                  });
                  setStartDate(momentStart.format(SAVE_DATE_FORMAT));
                  setEndDate(effectiveEnd.format(SAVE_DATE_FORMAT));
                }
              }}
              picker="month"
            />
          </FilterBoxCard>
          {getEuerElsterResponse.error && (
            <p>
              Sorry, that did not work ({getEuerElsterResponse.error.message})
            </p>
          )}
          <ButtonsWrapper>
            <Button
              icon={<DownloadOutlined />}
              disabled={!canExportBwaPDF}
              loading={isExportingPdf}
              onClick={handleDownloadPDFRequest}
              style={{ display: "inline-flex" }}
            >
              Download PDF
            </Button>
            <Button
              icon={<EyeOutlined />}
              disabled={!accountId}
              type="primary"
              onClick={handlePreviewRequest}
              style={{ display: "inline-flex" }}
            >
              Preview
            </Button>
            <Button
              icon={<DownloadOutlined />}
              disabled={!accountId}
              onClick={handleDownloadCSVRequest}
              style={{ display: "inline-flex" }}
            >
              Download CSV
            </Button>
          </ButtonsWrapper>
        </BWAHeaderContainer>
      </Row>

      <PreviewModal
        show={showModal}
        onCloseHandler={handleModalClose}
        accountId={accountId}
        startDate={startDate}
        endDate={endDate}
        euer={getEuerElsterResponse.data}
      />
    </>
  );
};

export default BWAHeader;
